import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { winRateColor } from "@/lib/signalColors";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Trophy, TrendingUp, Crown, Medal, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import WeeklyWinners from "@/components/WeeklyWinners";

export default function Leaderboard() {
  const navigate = useNavigate();
  const { isAuthenticated, authHeaders } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRank, setMyRank] = useState(null);
  const [optInDialog, setOptInDialog] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaderboard(response.data.leaderboard || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, []);

  const fetchMyRank = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/leaderboard/my-rank`, { headers });
      setMyRank(response.data);
      setDisplayName(response.data.display_name || "");
    } catch (error) { console.error("Failed to fetch rank:", error); }
  }, [authHeaders]);

  useEffect(() => {
    fetchLeaderboard();
    if (isAuthenticated) fetchMyRank();
  }, [isAuthenticated, fetchLeaderboard, fetchMyRank]);

  const handleOptIn = async () => {
    try {
      await axios.post(`${API}/leaderboard/opt-in`, {
        opted_in: true,
        display_name: displayName || "Anonymous Trader"
      }, { headers: authHeaders() });
      toast.success("You're on the leaderboard!");
      setOptInDialog(false);
      fetchLeaderboard();
      fetchMyRank();
    } catch (error) {
      toast.error("Failed to opt in");
    }
  };

  const handleOptOut = async () => {
    try {
      await axios.post(`${API}/leaderboard/opt-in`, {
        opted_in: false
      }, { headers: authHeaders() });
      toast.success("Removed from leaderboard");
      fetchLeaderboard();
      fetchMyRank();
    } catch (error) {
      toast.error("Failed to opt out");
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/leaderboard`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-[#F59E0B]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-[#CD7F32]" />;
    return <span className="text-sm text-gray-500 mono w-5 text-center">{rank}</span>;
  };

  return (
    <div className="min-h-screen bg-[#030712] text-white overflow-x-hidden">
      <div className="fixed inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F59E0B]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#6366F1]/5 rounded-full blur-3xl" />
      </div>

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="share-leaderboard-btn"
                size="sm"
                variant="outline"
                onClick={copyShareLink}
                className="border-white/10 text-gray-400 hover:text-white text-xs"
              >
                {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Share2 className="w-3.5 h-3.5 mr-1" />}
                {copied ? "Copied" : "Share"}
              </Button>
              {isAuthenticated && (
                myRank?.opted_in ? (
                  <Button
                    data-testid="opt-out-btn"
                    size="sm"
                    variant="outline"
                    onClick={handleOptOut}
                    className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs"
                  >
                    Leave Board
                  </Button>
                ) : (
                  <Button
                    data-testid="join-leaderboard-btn"
                    size="sm"
                    onClick={() => setOptInDialog(true)}
                    className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 text-xs font-bold"
                  >
                    <Trophy className="w-3.5 h-3.5 mr-1" />
                    Join Board
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <Trophy className="w-10 h-10 sm:w-12 sm:h-12 text-[#F59E0B] mx-auto mb-3" />
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>
              LEADERBOARD
            </h1>
            <p className="text-gray-400 text-sm">
              Top traders ranked by win rate & P&L
            </p>
          </div>

          <WeeklyWinners />

          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <Trophy className="w-8 h-8 mx-auto mb-4 animate-pulse text-[#F59E0B]" />
              Loading leaderboard...
            </div>
          ) : leaderboard.length === 0 ? (
            <Card className="glass-effect">
              <CardContent className="p-8 text-center">
                <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Traders Yet</h3>
                <p className="text-gray-400 mb-6 text-sm">
                  Be the first to join! Take trades from AI Signals, then opt into the leaderboard.
                </p>
                {isAuthenticated && !myRank?.opted_in && (
                  <Button
                    onClick={() => setOptInDialog(true)}
                    className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-bold"
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Join Leaderboard
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider">
                <div className="col-span-1">#</div>
                <div className="col-span-3">Trader</div>
                <div className="col-span-2 text-right">Win Rate</div>
                <div className="col-span-2 text-right">P&L</div>
                <div className="col-span-2 text-right">Avg Ret</div>
                <div className="col-span-2 text-right">W/L</div>
              </div>

              {leaderboard.map((entry, index) => (
                <motion.div
                  key={entry.display_name + '-' + entry.rank}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div
                    data-testid={`leaderboard-entry-${index}`}
                    className={`grid grid-cols-12 gap-2 px-3 py-3 rounded-lg items-center ${
                      entry.rank <= 3 ? 'glass-effect border border-[#F59E0B]/20' : 'glass-effect'
                    }`}
                  >
                    <div className="col-span-1 flex items-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="col-span-3">
                      <div className="text-sm font-semibold text-white truncate">{entry.display_name}</div>
                      <div className="text-[10px] text-gray-500">{entry.total_trades} trades</div>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className={`text-sm font-bold mono ${winRateColor(entry.win_rate)}`}>
                        {entry.win_rate}%
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className={`text-sm font-bold mono ${entry.total_pnl >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                        {entry.total_pnl >= 0 ? '+' : ''}${entry.total_pnl}
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className={`text-xs mono ${entry.avg_return >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                        {entry.avg_return >= 0 ? '+' : ''}{entry.avg_return}%
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-xs text-gray-300 mono">
                        <span className="text-[#00FFA3]">{entry.wins}</span>
                        <span className="text-gray-600">/</span>
                        <span className="text-[#FF3B30]">{entry.losses}</span>
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Opt-In Dialog */}
      <Dialog open={optInDialog} onOpenChange={setOptInDialog}>
        <DialogContent className="glass-solid max-w-[95vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-lg" style={{ fontFamily: 'Unbounded' }}>
              JOIN LEADERBOARD
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-gray-400">
              Your performance stats will be shown anonymously. Choose a display name:
            </p>
            <Input
              data-testid="display-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. CryptoKing, TradeMaster"
              className="bg-black/30 border-white/10 text-white"
            />
            <div className="glass-effect p-3 rounded-lg text-xs text-gray-400 space-y-1">
              <p>What's shared: win rate, P&L, avg return, trade count</p>
              <p>What's NOT shared: email, identity, specific trades</p>
            </div>
            <Button
              data-testid="confirm-opt-in-btn"
              onClick={handleOptIn}
              className="w-full bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-bold"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Join Leaderboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
